/* Mirror of the server's src/auth/permissions.js.
 *
 * Used only to hide controls a role cannot use — the server re-checks every
 * request, so this map is a UX convenience, never a security boundary. Keep
 * it in sync with the server when roles change. */

export const ROLES = ["owner", "admin", "staff", "driver"];

const ALL = [
  "workorders:read",
  "workorders:write",
  "workorders:delete",
  // Request, cancel, accept or reject moving a work order to another business
  "workorders:transfer",
  "funerals:read",
  "funerals:write",
  "products:read",
  "products:write",
  "inventory:read",
  "flowers:read",
  "flowers:write",
  "runsheets:read",
  "runsheets:write",
  "delivery:read",
  "delivery:write",
  "fd:read",
  "fd:write",
  "printers:use",
  "printers:manage",
  "settings:read",
  "settings:manage",
  "users:manage",
  "business:manage",
  "files:upload",
  "files:delete",
  "reports:read",
  "logs:read",
];

/* Owners alone hold these: admins run the workflow (orders, runs,
   products, settings) but never manage staff or the business. The server
   drops them from any non-owner membership, overrides included. */
export const OWNER_ONLY = ["business:manage", "users:manage"];

export const ROLE_PERMISSIONS = {
  owner: ALL,
  admin: ALL.filter((p) => !OWNER_ONLY.includes(p)),
  staff: [
    "workorders:read",
    "workorders:write",
    "funerals:read",
    "funerals:write",
    "products:read",
    "inventory:read",
    "flowers:read",
    "runsheets:read",
    "fd:read",
    "printers:use",
    "settings:read",
    "files:upload",
  ],
  driver: [
    "runsheets:read",
    "runsheets:write",
    "delivery:read",
    "delivery:write",
    "workorders:read",
  ],
};

export const ROLE_LABELS = {
  owner: "Owner · full access",
  admin: "Admin · workflow",
  staff: "Florist · bench view",
  driver: "Driver · deliveries",
};

/* Pre-RBAC sessions (and the server's legacy-token shim) still carry the old
   `access` strings. Map them so a user logged in across the deploy keeps a
   sensible UI until their token expires. */
const LEGACY_ACCESS = {
  "Super Admin": "owner",
  Admin: "admin",
  Staff: "staff",
};

export function roleOf(user) {
  const raw = (user || {}).role || (user || {}).access;
  if (!raw) return null;
  if (ROLES.includes(raw)) return raw;
  return LEGACY_ACCESS[raw] || "staff";
}

/** Owners — in any business they belong to — get the group overview;
    everyone else goes straight into their business. */
export function isGroupOwner(user, memberships) {
  if (roleOf(user) === "owner") return true;
  return (memberships || []).some((m) => roleOf(m) === "owner");
}

/** The permission set a role grants by default. */
export const permissionsForRole = (role) => [...(ROLE_PERMISSIONS[role] || [])];

/**
 * The permissions a user (or a membership row) actually holds. The server
 * lets an admin pin an explicit set on a membership that REPLACES the
 * role's defaults; sessions and user listings carry that as `permissions`.
 * Without it, fall back to the role map.
 */
export function permissionsOf(user) {
  const held =
    user && Array.isArray(user.permissions)
      ? user.permissions
      : permissionsForRole(roleOf(user));
  /* a session saved before the owner-only rule still lists them — the
     server refuses either way, so the UI must not offer them */
  return roleOf(user) === "owner" ? held : held.filter((p) => !OWNER_ONLY.includes(p));
}

export function can(user, permission) {
  return permissionsOf(user).includes(permission);
}

/** True when the held set differs from the role's defaults. */
export function isCustomAccess(user) {
  if (!user || !Array.isArray(user.permissions)) return false;
  const defaults = permissionsForRole(roleOf(user));
  const held = permissionsOf(user);
  return (
    held.length !== defaults.length || held.some((p) => !defaults.includes(p))
  );
}

export const roleLabel = (user) => {
  const base = ROLE_LABELS[roleOf(user)] || "No access";
  return isCustomAccess(user) ? `${base.split(" · ")[0]} · custom access` : base;
};

/* Short role names for tables and pills. */
export const ROLE_NAMES = {
  owner: "Owner",
  admin: "Admin",
  staff: "Florist",
  driver: "Driver",
};

export const ROLE_HELP = {
  owner: "Everything, including the business itself.",
  admin: "Orders, run sheets, products and settings — not staff.",
  staff: "Orders, products, run sheets and printing.",
  driver: "Run sheets and the delivery dashboard only.",
};

/* The access switches on the users page. Each switch flips a bundle of
   server permissions together — the raw list is too fine-grained for an
   admin to reason about, but nothing here invents a permission the
   server does not know. Sections only group the rows visually. */
export const ACCESS_AREAS = [
  {
    section: "Orders",
    rows: [
      { id: "orders-view", label: "See work orders", help: "Work order and funeral lists, order detail.", perms: ["workorders:read", "funerals:read"] },
      { id: "orders-edit", label: "Create and edit orders", help: "New orders, edits, attachments.", perms: ["workorders:write", "funerals:write", "files:upload"] },
      { id: "orders-delete", label: "Delete orders", help: "Remove orders and their attachments.", perms: ["workorders:delete", "files:delete"] },
      { id: "orders-transfer", label: "Transfer orders", help: "Send work orders to another business, and accept or decline requests from them.", perms: ["workorders:transfer"] },
    ],
  },
  {
    section: "Deliveries",
    rows: [
      { id: "runs-view", label: "See run sheets", help: "The delivery run list and run sheet detail.", perms: ["runsheets:read"] },
      { id: "runs-edit", label: "Build run sheets and deliver", help: "Create and edit runs, use the delivery dashboard.", perms: ["runsheets:write", "delivery:read", "delivery:write"] },
      { id: "printing", label: "Print labels and sheets", help: "Send jobs to the connected printers.", perms: ["printers:use"] },
      { id: "printers", label: "Manage printers", help: "Connect and configure printers.", perms: ["printers:manage"] },
    ],
  },
  {
    section: "Catalogue",
    rows: [
      { id: "catalogue-view", label: "See products and stock", help: "Products, inventory and flower lists.", perms: ["products:read", "inventory:read", "flowers:read"] },
      { id: "catalogue-edit", label: "Edit products and flowers", help: "Add and change products and flower prices.", perms: ["products:write", "flowers:write"] },
      { id: "fd-view", label: "See funeral directors", help: "The funeral director list.", perms: ["fd:read"] },
      { id: "fd-edit", label: "Edit funeral directors", help: "Add and change funeral directors.", perms: ["fd:write"] },
    ],
  },
  {
    section: "Administration",
    rows: [
      { id: "settings-view", label: "See settings", help: "Fees, product types and other settings.", perms: ["settings:read"] },
      { id: "settings-edit", label: "Change settings", help: "Edit fees, product types and lists.", perms: ["settings:manage"] },
      { id: "users", label: "Manage users and access", help: "Add people, change roles and these switches.", perms: ["users:manage"] },
      { id: "reports", label: "Group reports", help: "The group overview across businesses.", perms: ["reports:read"] },
      { id: "logs", label: "Error logs", help: "Server error log page.", perms: ["logs:read"] },
      { id: "business", label: "Manage the business", help: "Rename the business, create new ones.", perms: ["business:manage"] },
    ],
  },
];

/** A row is "on" when every permission in its bundle is held. */
export const areaOn = (row, held) => row.perms.every((p) => held.includes(p));

/** Toggle a bundle on or off inside a permission set (returns a new array). */
export function toggleArea(row, held, on) {
  const without = held.filter((p) => !row.perms.includes(p));
  return on ? [...without, ...row.perms] : without;
}

/* Every permission the switches can express — the server list, in ALL. */
export const ALL_PERMISSIONS = ALL;
